import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { downloadExport, getDashboard } from "./api";
import FilterPanel, { EMPTY_FILTERS, withChildrenCleared } from "./FilterPanel";
import KpiCards from "./KpiCards";
import BreakdownTable, { LEVEL_ORDER } from "./BreakdownTable";
import TrendChart from "./TrendChart";
import PunchDonut from "./PunchDonut";
import { ClientDistribution, WorkforceDistribution } from "./Distributions";
import { EmployeeTable, ExceptionTable } from "./EmployeeTables";
import EmployeeModal from "./EmployeeModal";
import { longDate, num } from "./format";
import "./attendanceV2.css";

const SAVED_FILTERS_KEY = "attendanceV2.savedFilters";
const ATTENTION_THRESHOLD = 80;
const EXCEPTIONS_PAGE = 8;

const readSavedFilters = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(SAVED_FILTERS_KEY)) || {};
    // only keys the panel still has, so filters saved by an older version are ignored
    return Object.fromEntries(Object.keys(EMPTY_FILTERS).map((k) => [k, saved[k] || ""]));
  } catch {
    return { ...EMPTY_FILTERS };
  }
};

// Deepest geography filter decides which level the location table shows
const levelFor = (filters) =>
  filters.zone ? "client" : filters.region ? "zone" : filters.state ? "region" : "state";

const useDashboardData = (path, params, refreshKey) => {
  const [state, setState] = useState({ data: null, loading: true });
  const key = JSON.stringify(params);
  useEffect(() => {
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true }));
    getDashboard(path, JSON.parse(key), controller.signal)
      .then((data) => setState({ data, loading: false }))
      .catch(() => !controller.signal.aborted && setState((s) => ({ ...s, loading: false })));
    return () => controller.abort();
  }, [path, key, refreshKey]);
  return state;
};

const useDebounced = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const AttendanceDashboard = () => {
  const sidebarOpen = useSelector((state) => state.SideBarActive.SidebarActive);
  const today = dayjs().format("YYYY-MM-DD");

  const [reportDate, setReportDate] = useState(today);
  const [applied, setApplied] = useState(readSavedFilters);
  const [draft, setDraft] = useState(applied);
  const [level, setLevel] = useState(() => levelFor(applied));
  const [refreshKey, setRefreshKey] = useState(0);

  const [trendRange, setTrendRange] = useState(7);
  const [customRange, setCustomRange] = useState({ from: dayjs().subtract(29, "day").format("YYYY-MM-DD"), to: today });
  const [distBy, setDistBy] = useState("designation");
  const [empTab, setEmpTab] = useState("all");
  const [empPage, setEmpPage] = useState(1);
  const [empSearch, setEmpSearch] = useState("");
  const [excType, setExcType] = useState("all");
  const [excSize, setExcSize] = useState(EXCEPTIONS_PAGE);
  const [openRow, setOpenRow] = useState(null);

  const employeesRef = useRef(null);
  const trendRef = useRef(null);
  const clientsRef = useRef(null);
  const search = useDebounced(empSearch, 400);

  const base = useMemo(() => ({ from_date: reportDate, to_date: reportDate, ...applied }), [reportDate, applied]);
  const trendParams = useMemo(() => {
    if (trendRange === "custom") return { ...applied, from_date: customRange.from, to_date: customRange.to };
    const from = dayjs(reportDate).subtract(trendRange - 1, "day").format("YYYY-MM-DD");
    return { ...applied, from_date: from, to_date: reportDate };
  }, [applied, reportDate, trendRange, customRange]);

  const options = useDashboardData("filters/", { from_date: reportDate, to_date: reportDate }, refreshKey);
  const summary = useDashboardData("summary/", base, refreshKey);
  const breakdown = useDashboardData("breakdown/", { ...base, level, threshold: ATTENTION_THRESHOLD }, refreshKey);
  const trend = useDashboardData("trend/", trendParams, refreshKey);
  const punch = useDashboardData("punch-status/", base, refreshKey);
  const clients = useDashboardData("clients/", base, refreshKey);
  const dist = useDashboardData("distribution/", { ...base, by: distBy }, refreshKey);
  const employees = useDashboardData(
    "employees/",
    { ...base, tab: empTab, page: empPage, page_size: 10, search },
    refreshKey
  );
  const exceptions = useDashboardData("exceptions/", { ...base, type: excType, page_size: excSize }, refreshKey);

  // Any change to what is being looked at starts the tables from the top
  useEffect(() => setEmpPage(1), [base, empTab, search]);
  useEffect(() => setExcSize(EXCEPTIONS_PAGE), [base, excType]);

  const nameOf = useCallback(
    (key, id) => {
      const list = { state: "states", region: "regions", zone: "zones", client: "locations", parent_client: "clients" }[key];
      return (options.data?.[list] || []).find((o) => o.id === id)?.name || "";
    },
    [options.data]
  );

  const isReportingPerson = !!options.data?.is_reporting_person;
  const orgLabel = isReportingPerson ? "My locations" : "Organization";

  // A reporting person only sees their own locations, so drop saved filters that point outside them
  useEffect(() => {
    if (!isReportingPerson) return;
    const lists = { state: "states", region: "regions", zone: "zones", client: "locations", parent_client: "clients" };
    const stale = Object.keys(lists).some(
      (k) => applied[k] && !(options.data?.[lists[k]] || []).some((o) => o.id === applied[k])
    );
    if (stale) {
      setApplied({ ...EMPTY_FILTERS });
      setDraft({ ...EMPTY_FILTERS });
      setLevel(levelFor(EMPTY_FILTERS));
    }
  }, [isReportingPerson, options.data, applied]);

  const applyFilters = (next) => {
    setApplied(next);
    setDraft(next);
    setLevel(levelFor(next));
  };

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const drill = (row) => {
    if (level === "client") {
      applyFilters({ ...applied, client: row.id });
      scrollTo(employeesRef);
      return;
    }
    const next = withChildrenCleared(applied, level, row.id);
    setApplied(next);
    setDraft(next);
    setLevel(LEVEL_ORDER[LEVEL_ORDER.indexOf(level) + 1]);
  };

  const pickTab = (tab) => {
    setEmpTab(tab);
    scrollTo(employeesRef);
  };

  const saveFilters = () => {
    try {
      localStorage.setItem(SAVED_FILTERS_KEY, JSON.stringify(applied));
      toast.success("Filters saved. They will load next time you open this page.");
    } catch {
      toast.error("Could not save filters in this browser.");
    }
  };

  const resetFilters = () => {
    try {
      localStorage.removeItem(SAVED_FILTERS_KEY);
    } catch {
      // nothing saved to clear
    }
    applyFilters({ ...EMPTY_FILTERS });
    toast.info(isReportingPerson ? "Filters reset to all your locations." : "Filters reset to organization view.");
  };

  const crumbs = [
    { key: null, label: orgLabel },
    ...["state", "region", "zone", "client"]
      .filter((k) => applied[k])
      .map((k) => ({ key: k, label: nameOf(k, applied[k]) || "…" })),
  ];
  const deepest = crumbs[crumbs.length - 1];
  const scopeLabel = deepest.key ? deepest.label : isReportingPerson ? "My locations" : "All locations";
  const levelBadge = deepest.key ? { state: "State", region: "Region", zone: "Zone", client: "Location" }[deepest.key] : orgLabel;
  const cov = summary.data?.coverage;
  const refreshedAt = summary.data?.data_refreshed_at;

  const goToCrumb = (key) => {
    const keep = key ? LEVEL_ORDER.slice(0, LEVEL_ORDER.indexOf(key) + 1) : [];
    const next = { ...applied };
    LEVEL_ORDER.forEach((k) => !keep.includes(k) && (next[k] = ""));
    applyFilters(next);
  };

  return (
    <div className={sidebarOpen ? "outterContent" : "inner-Content"}>
      <div className="attv2">
        <div className="a2-topbar">
          <div>
            <div className="a2-crumb">
              {crumbs.map((c, i) => (
                <React.Fragment key={c.key || "org"}>
                  {i > 0 && " / "}
                  {i < crumbs.length - 1 ? <a onClick={() => goToCrumb(c.key)}>{c.label}</a> : c.label}
                </React.Fragment>
              ))}
            </div>
            <div className="a2-titlerow">
              <span className="a2-level">{levelBadge}</span>
              <h1 className="a2-title">{deepest.key ? deepest.label : "All Employees"}</h1>
            </div>
            <div className="a2-sub">
              {longDate(reportDate)}
              {cov && ` · ${num(cov.states)} states · ${num(cov.regions)} regions · ${num(cov.zones)} zones · ${num(cov.clients)} clients`}
            </div>
          </div>
          <div className="a2-topbar-right a2-noprint">
            <div className="a2-updated">
              <b>{longDate(reportDate)}</b>
              {refreshedAt ? `Data refreshed ${dayjs(refreshedAt).format("hh:mm A")}` : "Not calculated yet"}
            </div>
            <button className="a2-btn a2-icon-btn" title="Reload" aria-label="Reload" onClick={() => setRefreshKey((k) => k + 1)}>
              <RefreshIcon />
            </button>
            <button className="a2-btn a2-btn-primary" onClick={() => downloadExport({ ...base, level, tab: empTab })}>
              <FileDownloadOutlinedIcon /> Export
            </button>
          </div>
        </div>

        <div className="a2-actions a2-noprint">
          <button className="a2-btn" onClick={() => scrollTo(employeesRef)}><PeopleOutlineIcon /> View Employees</button>
          <button className="a2-btn" onClick={() => scrollTo(trendRef)}><BarChartOutlinedIcon /> View Attendance</button>
          <button className="a2-btn" onClick={() => scrollTo(clientsRef)}><ApartmentOutlinedIcon /> View Clients</button>
          {/* <button className="a2-btn" onClick={() => window.print()}><PrintOutlinedIcon /> Download PDF</button> */}
          <button className="a2-btn" onClick={() => downloadExport({ ...base, level, tab: empTab })}>
            <FileDownloadOutlinedIcon /> Download Excel
          </button>
        </div>

        <FilterPanel
          reportDate={reportDate}
          onReportDate={setReportDate}
          draft={draft}
          setDraft={setDraft}
          applied={applied}
          options={options.data}
          onApply={() => applyFilters(draft)}
          onClear={() => applyFilters({ ...EMPTY_FILTERS })}
          onReset={resetFilters}
          onSave={saveFilters}
          onRemoveApplied={(key) => applyFilters(withChildrenCleared(applied, key, ""))}
        />

        <KpiCards summary={summary.data} threshold={ATTENTION_THRESHOLD} onPick={pickTab} />

        <BreakdownTable
          data={breakdown.data}
          loading={breakdown.loading}
          level={level}
          onLevel={setLevel}
          onDrill={drill}
        />

        <div className="a2-grid-2" ref={trendRef}>
          <TrendChart
            data={trend.data}
            loading={trend.loading}
            range={trendRange}
            onRange={setTrendRange}
            custom={customRange}
            onCustom={setCustomRange}
          />
          <PunchDonut data={punch.data} loading={punch.loading} activeTab={empTab} onPick={pickTab} />
        </div>

        <div className="a2-grid-2-even" ref={clientsRef}>
          <ClientDistribution
            data={clients.data}
            loading={clients.loading}
            onPick={(row) => applyFilters(withChildrenCleared(applied, "parent_client", row.id))}
          />
          <WorkforceDistribution data={dist.data} loading={dist.loading} by={distBy} onBy={setDistBy} />
        </div>

        <EmployeeTable
          innerRef={employeesRef}
          data={employees.data}
          loading={employees.loading}
          tab={empTab}
          onTab={setEmpTab}
          search={empSearch}
          onSearch={setEmpSearch}
          onPage={setEmpPage}
          onOpen={setOpenRow}
          multiDay={false}
          scopeLabel={scopeLabel}
        />

        <ExceptionTable
          data={exceptions.data}
          loading={exceptions.loading}
          type={excType}
          onType={setExcType}
          onMore={excSize < 100 ? () => setExcSize((n) => Math.min(100, n + 20)) : null}
          onOpen={setOpenRow}
          multiDay={false}
          scopeLabel={scopeLabel}
        />

        {openRow && (
          <EmployeeModal
            row={openRow}
            reportDate={reportDate}
            onClose={() => setOpenRow(null)}
            onOpenClient={(row) => {
              setOpenRow(null);
              applyFilters({ ...EMPTY_FILTERS, parent_client: row.client_uuid });
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AttendanceDashboard;
